/**
 * Jenny WhatsApp — sincronización de variante
 *
 * Módulo independiente y aditivo. Solo lee datos del producto/variante actual
 * y actualiza el href de un único enlace. No interviene en carrito, checkout,
 * ni en la lógica nativa de variantes, add-to-cart o accelerated-checkout.
 *
 * NOTA: no se usa `import { ThemeEvents } from '@theme/events'` porque no se
 * puede garantizar que ese alias de módulo resuelva igual en toda instalación
 * de Horizon. En su lugar se escucha directamente el nombre de evento nativo
 * que Horizon despacha en el DOM ('variant:update'), evitando cualquier
 * dependencia de imports internos del tema.
 */

class JennyWhatsappButton extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('[data-jenny-link]');
    this.sectionRoot = this.closest('.shopify-section, dialog') || document;

    this._onVariantUpdate = this.#handleVariantUpdate.bind(this);
    this.sectionRoot.addEventListener('variant:update', this._onVariantUpdate);
  }

  disconnectedCallback() {
    this.sectionRoot.removeEventListener('variant:update', this._onVariantUpdate);
  }

  /**
   * @param {CustomEvent} event - evento variant:update emitido por Horizon
   *
   * Patrón confirmado: Horizon entrega en event.detail.data un objeto que,
   * cuando hay producto/variante nueva, incluye data.newProduct (datos del
   * producto) y data.html (fragmento HTML re-renderizado en servidor con
   * los valores ya correctos de la nueva variante). En vez de asumir la forma
   * exacta de un objeto "variant" suelto, leemos el mismo fragmento `data.html`
   * que el propio tema usa para refrescar el resto de la página — así el
   * módulo se mantiene alineado con lo que Horizon realmente entrega,
   * en lugar de una estructura de datos no verificada.
   */
  #handleVariantUpdate(event) {
    const { data } = event.detail || {};
    if (!data) return;

    const target = event.target;
    if (!data.newProduct && target instanceof HTMLElement && this.dataset.productId) {
      if (target.dataset.productId !== this.dataset.productId) return;
    }
    if (data.newProduct?.id) {
      this.dataset.productId = data.newProduct.id;
    }

    if (!data.html) return;

    // Buscar, dentro del fragmento re-renderizado, la misma instancia de este
    // bloque (mismo custom element) para extraer sus data-attributes ya
    // actualizados por Liquid con la variante correcta.
    const incoming = data.html.querySelector('jenny-whatsapp-button');
    if (!incoming) return;

    const incomingLink = incoming.querySelector('[data-jenny-link]');
    if (!incomingLink || !this.link) return;

    this.link.setAttribute('href', incomingLink.getAttribute('href'));
  }
}

if (!customElements.get('jenny-whatsapp-button')) {
  customElements.define('jenny-whatsapp-button', JennyWhatsappButton);
}
